import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ProductInterestCTA from '../../components/feature/ProductInterestCTA';
import { inspirationProjects } from '../../mocks/products';

export default function HomePage() {
  const { t } = useTranslation();

  // Categories with i18n
  const categories = [
    {
      id: 1,
      slug: 'vrijstaande-haarden',
      key: 'freestandingFireplaces',
      image: 'https://sfeerhaardkopen.com/cdn/shop/files/VerdiFires0.jpg?v=1766689629'
    },
    {
      id: 2,
      slug: 'inbouwhaarden',
      key: 'builtIn',
      image: 'https://diamondflame.nl/cdn/shop/files/RoyalDiamond1_774bf3ad-1297-4eaf-b41e-f1f18df8e26a.png'
    },
    {
      id: 3,
      slug: '3-zijdige-haarden',
      key: 'threeSided',
      image: 'https://diamondflame.nl/cdn/shop/files/DeepDiamondhaard.jpg?v=1759795057'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center">
        {/* Background Image - Dutch Fire Royal Diamond in luxury setting */}
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/316cd4d2123c34c39ccb150e8e5b4c79.jpg"
            alt="Hero Background"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 w-full py-32">
          <div className="max-w-2xl">
            <div className="inline-block px-4 py-2 bg-amber-500/90 rounded-full mb-6">
              <span className="text-xs font-semibold uppercase tracking-wider text-black">
                {t('common:hero.badge')}
              </span>
            </div>

            <h1 className="text-6xl lg:text-7xl font-bold text-white mb-4 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
              {t('common:hero.title')}<br />
              {t('common:hero.titleSecond')}
            </h1>

            <p className="text-lg text-gray-200 mb-8 leading-relaxed max-w-xl">
              {t('common:hero.subtitle')}
            </p>

            <div className="flex flex-wrap gap-4">
              <Link
                to="/dealer-registration"
                className="px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-black font-semibold rounded-full hover:from-amber-600 hover:to-amber-700 transition-all shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap inline-flex items-center gap-2"
              >
                {t('common:hero.ctaPrimary')}
                <i className="ri-arrow-right-line"></i>
              </Link>
              <button
                onClick={() => document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' })}
                className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-full hover:bg-white/20 transition-all border border-white/30 cursor-pointer whitespace-nowrap"
              >
                {t('common:hero.ctaSecondary')}
              </button>
            </div>

            {/* Stats */}
            <div className="mt-12 flex flex-wrap gap-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl px-6 py-4 border border-white/20">
                <div className="text-3xl font-bold text-white mb-1">500+</div>
                <div className="text-sm text-gray-300">{t('common:hero.stats.dealers')}</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl px-6 py-4 border border-white/20">
                <div className="text-3xl font-bold text-white mb-1">15</div>
                <div className="text-sm text-gray-300">{t('common:hero.stats.countries')}</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl px-6 py-4 border border-white/20">
                <div className="text-sm text-gray-300">{t('common:hero.stats.certified')}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* USP Bar */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: 'ri-star-line', key: 'europeanBrand' },
              { icon: 'ri-truck-line', key: 'fastDelivery' },
              { icon: 'ri-line-chart-line', key: 'highMargins' },
              { icon: 'ri-customer-service-2-line', key: 'fullSupport' }
            ].map((usp, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <i className={`${usp.icon} text-5xl text-amber-600`} style={{ width: '48px', height: '48px' }}></i>
                </div>
                <h3 className="text-base font-semibold text-gray-900 mb-2">
                  {t(`common:usps.${usp.key}.title`)}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {t(`common:usps.${usp.key}.description`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Categories */}
      <section id="products" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              {t('common:categories.title')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {categories.map((category) => (
              <Link
                key={category.id}
                to={`/category/${category.slug}`}
                className="group relative rounded-2xl overflow-hidden cursor-pointer hover:scale-[1.02] transition-all duration-300"
              >
                <div className="aspect-[4/5] relative">
                  <img
                    src={category.image}
                    alt={t(`common:categories.${category.key}`)}
                    className="w-full h-full object-cover group-hover:brightness-110 transition-all duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                </div>

                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <h3 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {t(`common:categories.${category.key}`)}
                  </h3>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-white/80">
                      {t('common:categories.viewProducts')}
                    </span>
                    <i className="ri-arrow-right-line text-white text-xl transition-transform group-hover:translate-x-1"></i>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why Dutch Fire */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-1.5 bg-amber-100 rounded-full mb-4">
              <span className="text-xs font-semibold uppercase tracking-wider text-amber-700">
                {t('common:whyDutchFire.label')}
              </span>
            </div>
            <h2 className="text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              {t('common:whyDutchFire.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {t('common:whyDutchFire.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {['wholesalePrices', 'marketingSupport', 'technicalTraining'].map((feature) => (
              <div key={feature} className="bg-white rounded-3xl p-12 shadow-sm hover:shadow-lg transition-shadow">
                <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mb-6 mx-auto">
                  <i className={`${
                    feature === 'wholesalePrices' ? 'ri-price-tag-3-line' :
                    feature === 'marketingSupport' ? 'ri-megaphone-line' :
                    'ri-graduation-cap-line'
                  } text-4xl text-amber-600`} style={{ width: '36px', height: '36px' }}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">
                  {t(`common:whyDutchFire.${feature}.title`)}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed mb-4 text-center">
                  {t(`common:whyDutchFire.${feature}.description`)}
                </p>
                <ul className="space-y-2">
                  {[0, 1, 2].map((i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                      <i className="ri-checkbox-circle-fill text-amber-600 mt-0.5 flex-shrink-0"></i>
                      <span>{t(`common:whyDutchFire.${feature}.benefits.${i}`)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Inspiration Gallery */}
      <section className="py-24 bg-gray-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-1.5 bg-amber-500/20 rounded-full mb-4">
              <span className="text-xs font-semibold uppercase tracking-wider text-amber-400">
                {t('common:inspiration.label')}
              </span>
            </div>
            <h2 className="text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              {t('common:inspiration.title')}
            </h2>
            <p className="text-lg text-gray-400 max-w-3xl mx-auto">
              {t('common:inspiration.subtitle')}
            </p>
          </div>

          {/* Cinewall Project Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            {inspirationProjects.slice(0, 2).map((project) => (
              <Link
                key={project.id}
                to="/dealer-registration"
                className="group relative rounded-2xl overflow-hidden cursor-pointer"
              >
                <div className="aspect-[4/3] relative">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-3 py-1 bg-amber-500/90 rounded-full text-xs font-semibold text-black">
                      {project.model}
                    </span>
                  </div>
                  <h3 className="text-2xl font-semibold text-white mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {project.title}
                  </h3>
                  <p className="text-sm text-gray-300 mb-4">{project.location}</p>
                  <span className="inline-flex items-center gap-2 text-sm text-amber-400 group-hover:text-amber-300 transition-colors">
                    {t('common:inspiration.viewPossibilities')}
                    <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                  </span>
                </div>
              </Link>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {inspirationProjects.slice(2, 6).map((project) => (
              <Link
                key={project.id}
                to="/dealer-registration"
                className="group relative rounded-xl overflow-hidden cursor-pointer"
              >
                <div className="aspect-[3/4] relative">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <span className="inline-block px-2 py-0.5 bg-amber-500/90 rounded text-xs font-medium text-black mb-2">
                    {project.model}
                  </span>
                  <h3 className="text-lg font-semibold text-white mb-0.5">
                    {project.title}
                  </h3>
                  <p className="text-xs text-gray-400">{project.location}</p>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center">
            <Link
              to="/inspiration"
              className="inline-block px-8 py-4 bg-transparent border-2 border-white text-white font-semibold rounded-full hover:bg-white hover:text-gray-900 transition-all cursor-pointer whitespace-nowrap"
            >
              {t('common:inspiration.viewAll')}
            </Link>
          </div>
        </div>
      </section>

      {/* Dealer Benefits */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
            <div className="lg:col-span-3">
              <h2 className="text-5xl font-bold text-gray-900 mb-6 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                {t('common:dealerBenefits.title')}
              </h2>
              <p className="text-base text-gray-600 leading-relaxed mb-10 max-w-xl">
                {t('common:dealerBenefits.intro')}
              </p>

              <div className="space-y-6">
                {[1, 2, 3, 4, 5].map((num) => (
                  <div key={num} className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-amber-500 flex items-center justify-center">
                      <span className="text-xl font-bold text-black">{num}</span>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        {t(`common:dealerBenefits.benefit${num}.title`)}
                      </h3>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {t(`common:dealerBenefits.benefit${num}.description`)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <Link
                to="/dealer-registration"
                className="inline-block mt-10 px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-black font-semibold rounded-full hover:from-amber-600 hover:to-amber-700 transition-all shadow-lg cursor-pointer whitespace-nowrap"
              >
                {t('common:dealerBenefits.cta')}
              </Link>
            </div>

            <div className="lg:col-span-2">
              <div className="bg-white rounded-3xl p-14 shadow-xl border border-gray-100">
                <div className="space-y-10">
                  <div className="text-center">
                    <div className="text-7xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>500+</div>
                    <div className="text-sm text-gray-600">{t('common:dealerBenefits.stats.dealers')}</div>
                  </div>
                  <div className="h-0.5 bg-amber-500 w-16 mx-auto"></div>
                  <div className="text-center">
                    <div className="text-7xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>15</div>
                    <div className="text-sm text-gray-600">{t('common:dealerBenefits.stats.countries')}</div>
                  </div>
                  <div className="h-0.5 bg-amber-500 w-16 mx-auto"></div>
                  <div className="text-center">
                    <div className="text-7xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>98%</div>
                    <div className="text-sm text-gray-600">{t('common:dealerBenefits.stats.satisfaction')}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dealer Process */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-1.5 bg-green-100 rounded-full mb-4">
              <span className="text-xs font-semibold uppercase tracking-wider text-green-700">
                {t('common:dealerProcess.badge')}
              </span>
            </div>
            <h2 className="text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              {t('common:dealerProcess.title')}
            </h2>
          </div>

          <div className="relative">
            <div className="absolute top-16 left-0 right-0 h-0.5 bg-amber-200 hidden lg:block" style={{ top: '32px' }}></div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-12 relative">
              {[1, 2, 3].map((step) => (
                <div key={step} className="text-center">
                  <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-white border-4 border-amber-500 flex items-center justify-center relative z-10">
                    <span className="text-2xl font-bold text-gray-900">{step}</span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    {t(`common:dealerProcess.step${step}.title`)}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed mb-3 max-w-xs mx-auto">
                    {t(`common:dealerProcess.step${step}.description`)}
                  </p>
                  <span className="text-xs text-amber-600 font-medium">
                    {t(`common:dealerProcess.step${step}.time`)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center mt-12">
            <p className="text-sm text-gray-600 mb-6">{t('common:dealerProcess.reassurance')}</p>
            <Link
              to="/dealer-registration"
              className="inline-block px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-black font-semibold rounded-full hover:from-amber-600 hover:to-amber-700 transition-all shadow-lg cursor-pointer whitespace-nowrap"
            >
              {t('common:dealerProcess.cta')}
            </Link>
          </div>
        </div>
      </section>

      {/* Resources */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              {t('common:resources.support.title')}
            </h2>
          </div>
          
          <div className="max-w-xl mx-auto">
            <div className="bg-gray-50 rounded-2xl p-8 text-center">
              <p className="text-base text-gray-700 mb-6">
                {t('common:resources.support.contactText')}
              </p>
              <div className="flex items-center justify-center gap-3 mb-3">
                <i className="ri-mail-line text-2xl text-amber-600"></i>
                <a href="mailto:info@dutchfires.de" className="text-lg font-semibold text-gray-900 hover:text-amber-600 transition-colors cursor-pointer">
                  info@dutchfires.de
                </a>
              </div>
              <p className="text-sm text-gray-600">{t('common:resources.support.emailResponse')}</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
